import type { GraphNode, GraphLink, GraphData, EntityType, Note, AINote, Person, Project, Task, Edge, Mood, Body, Food, Activity, Resource, Reflection } from '../types'

export const ENTITY_COLORS: Record<EntityType, string> = {
  note:       '#ffffff',  // white
  ai_note:    '#a0a0a0',  // gray
  person:     '#60a5fa',  // blue
  project:    '#fbbf24',  // amber
  task:       '#f87171',  // red
  mood:       '#c084fc',  // purple
  body:       '#34d399',  // emerald
  food:       '#fb923c',  // orange
  activity:   '#22d3ee',  // cyan
  resource:   '#a3e635',  // lime
  reflection: '#e879f9',  // fuchsia
}

export const RELATIONSHIP_COLORS: Record<string, string> = {
  // Note → extraction entities (color matches target node type)
  HAS_MOOD:        '#c084fc',  // purple
  HAS_BODY:        '#34d399',  // emerald
  HAS_FOOD:        '#fb923c',  // orange
  HAS_ACTIVITY:    '#22d3ee',  // cyan
  HAS_RESOURCE:    '#a3e635',  // lime
  HAS_REFLECTION:  '#e879f9',  // fuchsia
  HAS_TASK:        '#f87171',  // red
  // People / mentions
  MENTIONS:        '#60a5fa',  // blue
  MENTIONS_PERSON: '#60a5fa',  // blue
  MENTIONED_BY:    '#60a5fa',  // blue
  MENTIONED_IN_NOTE: '#60a5fa', // blue
  // Chronological
  AFTER:           '#4b5563',  // gray-600
  BEFORE:          '#4b5563',  // gray-600
  // Project / task relations
  BELONGS_TO:      '#fbbf24',  // amber
  WORKS_ON:        '#fbbf24',  // amber
  WORKED_ON_BY:    '#fbbf24',  // amber
  // Creation
  CREATED:         '#93c5fd',  // light blue
  CREATED_BY:      '#93c5fd',  // light blue
  // Generic relations
  RELATED_TO:      '#9ca3af',  // gray-400
  PART_OF:         '#d1d5db',  // gray-300
  CONTAINS:        '#d1d5db',  // gray-300
  DEPENDS_ON:      '#fca5a5',  // red-300
  REQUIRED_BY:     '#fca5a5',  // red-300
  // Activity reverse
  PERFORMED_IN_NOTE: '#22d3ee', // cyan
}

export function getRelationshipColor(relationship: string): string {
  return RELATIONSHIP_COLORS[relationship] ?? 'rgba(255,255,255,0.25)'
}

export const ENTITY_SIZES: Record<EntityType, number> = {
  note: 4, ai_note: 5, person: 6, project: 7, task: 5,
  mood: 4, body: 4, food: 4, activity: 4, resource: 4, reflection: 4,
}

export function createNodeId(entityType: EntityType, entityId: number): string {
  return `${entityType}:${entityId}`
}

export function parseNodeId(nodeId: string): { entityType: EntityType; entityId: number } | null {
  const parts = nodeId.split(':')
  if (parts.length !== 2) return null
  return { entityType: parts[0] as EntityType, entityId: parseInt(parts[1], 10) }
}

type AnyEntity = Note | AINote | Person | Project | Task | Mood | Body | Food | Activity | Resource | Reflection

export function getNodeLabel(entityType: EntityType, data: AnyEntity): string {
  switch (entityType) {
    case 'note':       return (data as Note).text.slice(0, 30) + ((data as Note).text.length > 30 ? '...' : '')
    case 'ai_note':    return (data as AINote).content.slice(0, 30) + ((data as AINote).content.length > 30 ? '...' : '')
    case 'person':     return (data as Person).name
    case 'project':    return (data as Project).name
    case 'task':       return (data as Task).title
    case 'mood': {
      const m = data as Mood
      const val = m.valence != null ? Number(m.valence) : null
      const v = val != null ? (val > 0 ? '+' : '') + val.toFixed(1) : '?'
      return `Mood ${v}`
    }
    case 'body': {
      const b = data as Body
      return b.physical || b.sleep || b.substance || 'Body'
    }
    case 'food': {
      const f = data as Food
      return f.items?.slice(0, 2).join(', ') || f.meal || 'Food'
    }
    case 'activity':   return (data as Activity).description || (data as Activity).activity_type || 'Activity'
    case 'resource':   return (data as Resource).title || (data as Resource).url || 'Resource'
    case 'reflection': return (data as Reflection).content.slice(0, 40) + ((data as Reflection).content.length > 40 ? '...' : '')
    default:           return 'Unknown'
  }
}

export function getNodeDescription(entityType: EntityType, data: AnyEntity): string {
  switch (entityType) {
    case 'note':       return (data as Note).text
    case 'ai_note':    return (data as AINote).content
    case 'person':     return (data as Person).context || ''
    case 'project':    return (data as Project).description || ''
    case 'task':       return (data as Task).description || ''
    case 'mood': {
      const m = data as Mood
      const parts: string[] = []
      if (m.emotions?.length) parts.push(m.emotions.join(', '))
      if (m.trigger) parts.push(`trigger: ${m.trigger}`)
      return parts.join(' · ')
    }
    case 'body': {
      const b = data as Body
      return [b.physical, b.sleep, b.substance].filter(Boolean).join(' · ')
    }
    case 'food': {
      const f = data as Food
      return [f.items?.join(', '), f.observation].filter(Boolean).join(' · ')
    }
    case 'activity': {
      const a = data as Activity
      return [a.description, a.location, a.duration_estimate].filter(Boolean).join(' · ')
    }
    case 'resource': {
      const r = data as Resource
      return [r.title, r.url, r.description].filter(Boolean).join(' · ')
    }
    case 'reflection': return (data as Reflection).content
    default:           return ''
  }
}

export function transformToGraphData(
  notes: Note[],
  aiNotes: AINote[],
  people: Person[],
  projects: Project[],
  tasks: Task[],
  edges: Edge[],
  moods: Mood[] = [],
  bodies: Body[] = [],
  foods: Food[] = [],
  activities: Activity[] = [],
  resources: Resource[] = [],
  reflections: Reflection[] = [],
): GraphData {
  const nodes: GraphNode[] = []
  const links: GraphLink[] = []

  function addNodes<T extends AnyEntity>(type: EntityType, items: T[]) {
    items.forEach(item => {
      nodes.push({
        id: createNodeId(type, (item as any).id),
        entityType: type,
        entityId: (item as any).id,
        label: getNodeLabel(type, item),
        description: getNodeDescription(type, item),
        color: ENTITY_COLORS[type],
        size: ENTITY_SIZES[type],
        data: item,
      })
    })
  }

  addNodes('note', notes)
  addNodes('ai_note', aiNotes)
  addNodes('person', people)
  addNodes('project', projects)
  addNodes('task', tasks)
  addNodes('mood', moods)
  addNodes('body', bodies)
  addNodes('food', foods)
  addNodes('activity', activities)
  addNodes('resource', resources)
  addNodes('reflection', reflections)

  // Task → project links
  tasks.forEach(task => {
    if (task.project_id) {
      links.push({
        source: createNodeId('task', task.id),
        target: createNodeId('project', task.project_id),
        relationship: 'BELONGS_TO',
        color: getRelationshipColor('BELONGS_TO'),
      })
    }
  })

  // AGE + relational edges
  const nodeSet = new Set(nodes.map(n => n.id))
  edges.forEach(edge => {
    const src = createNodeId(edge.from_type as EntityType, edge.from_id)
    const tgt = createNodeId(edge.to_type as EntityType, edge.to_id)
    if (nodeSet.has(src) && nodeSet.has(tgt)) {
      links.push({
        source: src,
        target: tgt,
        relationship: edge.relationship,
        color: getRelationshipColor(edge.relationship),
        edgeId: edge.id,
      })
    }
  })

  return { nodes, links }
}
